import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPhone, FaWhatsapp, FaEnvelope, FaMapMarkerAlt, FaUser, FaCommentDots, FaPaperPlane } from 'react-icons/fa';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      alert('Thank you for your message! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setIsSubmitting(false);
    }, 2000);
  };

  const contactInfo = [
    {
      icon: FaPhone,
      title: 'Phone Numbers',
      details: ['8883686347', '9994501212'],
      color: '#667eea'
    },
    {
      icon: FaWhatsapp,
      title: 'WhatsApp',
      details: ['9659377555'],
      color: '#25d366'
    },
    {
      icon: FaEnvelope,
      title: 'Email',
      details: ['sarvatmanlifescience@gmail.com'],
      color: '#ed8936'
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Address',
      details: ['Coimbatore - 641010'],
      color: '#e53e3e'
    },
    {
      icon: FaEnvelope,
      title: 'Website',
      details: ['www.sarvatmanlifescience.com'],
      color: '#E67E22'
    }
  ];

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-background">
          <div className="contact-hero-overlay"></div>
        </div>
        <div className="container">
          <motion.div
            className="contact-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="contact-hero-title">Get In Touch</h1>
            <p className="contact-hero-subtitle">
              We're here to help and answer any questions you might have. 
              We look forward to hearing from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="contact-info-section section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Contact Information</h2>
            <p className="section-subtitle">
              Reach out to us through any of the following channels
            </p>
          </motion.div>

          <div className="contact-info-grid">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                className="contact-info-card"
                style={{ '--contact-color': info.color }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                viewport={{ once: true }}
              >
                <div className="contact-info-icon">
                  <info.icon />
                </div>
                <h3 className="contact-info-title">{info.title}</h3>
                <div className="contact-info-details">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="contact-detail">{detail}</p>
                  ))}
                </div>
                {info.title === 'WhatsApp' && (
                  <a 
                    href={`https://wa.me/91${info.details[0]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-action-btn"
                  >
                    Chat Now
                  </a>
                )}
                {info.title === 'Email' && (
                  <a 
                    href={`mailto:${info.details[0]}`}
                    className="contact-action-btn"
                  >
                    Send Email
                  </a>
                )}
                {info.title === 'Phone Numbers' && (
                  <a 
                    href={`tel:${info.details[0]}`}
                    className="contact-action-btn"
                  >
                    Call Now
                  </a>
                )}
                {info.title === 'Website' && (
                  <a 
                    href={`https://${info.details[0]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-action-btn"
                  >
                    Visit Website
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section section">
        <div className="container">
          <div className="contact-form-grid">
            <motion.div
              className="form-info"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="form-info-title">Send Us a Message</h2>
              <p className="form-info-description">
                Have a question about our products or services? We'd love to hear from you. 
                Fill out the form and we'll get back to you as soon as possible.
              </p>
              
              <div className="form-features">
                <div className="form-feature">
                  <FaCommentDots className="form-feature-icon" />
                  <span>Quick Response</span>
                </div>
                <div className="form-feature">
                  <FaUser className="form-feature-icon" />
                  <span>Expert Support</span>
                </div>
                <div className="form-feature">
                  <FaPaperPlane className="form-feature-icon" />
                  <span>24/7 Available</span>
                </div>
              </div>

              <div className="office-hours">
                <h3>Office Hours</h3>
                <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                <p>Saturday: 9:00 AM - 2:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </motion.div>

            <motion.div
              className="contact-form-container"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="subject" className="form-label">Subject *</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="What is this about?"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-textarea"
                    rows="6"
                    required
                    placeholder="Tell us more about your inquiry..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className={`form-submit-btn ${isSubmitting ? 'submitting' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <motion.div
            className="map-container"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="map-placeholder">
              <FaMapMarkerAlt className="map-icon" />
              <h3>Our Location</h3>
              <p>Coimbatore - 641010, Tamil Nadu, India</p>
              <p>We are centrally located for easy access</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
